// v3.21-1729 · Centralized authenticated API client + refresh lifecycle.
//
//   ROOT CAUSE (v1728→v1729):
//     The v1684 interceptor gated auto-refresh on `memoryAccessToken !== null`,
//     but `AuthContext.fetchUser` only read the access token from
//     sessionStorage on page-reload and never called `setAccessToken()` to
//     seed the in-memory copy.  Result: after any page reload with an
//     access token about to expire, the interceptor sent the stale token,
//     received 401, saw memory == null, and refused to refresh → the raw
//     "Invalid or expired token" bubbled all the way into the UI (Save
//     Coaching Note was the reported instance; every save/update path had
//     the same defect).
//
//   ARCHITECTURE (unchanged intent, hardened):
//     • Access token lives in memory (setAccessToken) + short-term
//       sessionStorage for in-tab reload restoration.  The durable
//       credential is the HttpOnly `hub_refresh` cookie on
//       /api/auth/session/*.
//     • Every request runs with `withCredentials: true`.
//     • A single-flight refresh queue prevents concurrent 401s from
//       firing multiple rotations (which would invalidate our own
//       refresh token).
//     • ONE retry attempt per original request — never call /refresh
//       from /refresh (infinite-loop guard).
//     • If refresh itself returns 401, notify the app via
//       `onSessionExpired` callback so a single friendly message is
//       shown and the user is routed to /login — never expose the raw
//       backend "Invalid or expired token" text.
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_RETRIES = 2;
const RETRY_DELAY = 1000;
const failedRequests = [];

// ── In-memory access token ────────────────────────────────────────────
let memoryAccessToken = null;
export const setAccessToken = (tok) => {
  memoryAccessToken = tok || null;
  if (tok) sessionStorage.setItem('access_token', tok);
};
export const getAccessToken = () => memoryAccessToken
  || sessionStorage.getItem('access_token')
  || localStorage.getItem('access_token');

// ── One-shot session-expired notifier (registered by AuthContext) ────
let sessionExpiredHandler = null;
export const registerSessionExpiredHandler = (fn) => { sessionExpiredHandler = fn; };
let sessionExpiredFired = false;
const fireSessionExpired = () => {
  if (sessionExpiredFired) return;
  sessionExpiredFired = true;
  try { sessionExpiredHandler && sessionExpiredHandler(); } catch (_e) { /* noop */ }
  setTimeout(() => { sessionExpiredFired = false; }, 10000);
};

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    // Prefer the in-memory token; fall back to sessionStorage/localStorage
    // strictly for in-tab reload restoration before AuthContext boots.
    const memTok = memoryAccessToken
      || sessionStorage.getItem('access_token')
      || localStorage.getItem('access_token');
    if (memTok) config.headers.Authorization = `Bearer ${memTok}`;
    // Seed memory from storage if it was empty (fixes the page-reload
    // refresh-gate bug — see ROOT CAUSE comment above).
    if (!memoryAccessToken && memTok) memoryAccessToken = memTok;
    config.__retryCount = config.__retryCount || 0;
    config.__startTime  = Date.now();
    config.__requestId  = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Single-flight refresh queue ────────────────────────────────────────
let refreshInFlight = null;         // Promise<newAccessToken | null>
const refreshWaiters = [];          // queued original-request retriers

const performRefresh = () => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = axios.post(
    `${BACKEND_URL}/api/auth/session/refresh`,
    {},
    { withCredentials: true, timeout: 15000 },
  ).then((res) => {
    const newTok = res.data?.access_token || null;
    memoryAccessToken = newTok;
    if (newTok) sessionStorage.setItem('access_token', newTok);
    refreshWaiters.splice(0).forEach(fn => fn(newTok));
    return newTok;
  }).catch((err) => {
    // Fail closed: clear waiters and every local token copy.
    memoryAccessToken = null;
    sessionStorage.removeItem('access_token');
    localStorage.removeItem('access_token');
    refreshWaiters.splice(0).forEach(fn => fn(null));
    throw err;
  }).finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};
    const status = error.response?.status;
    const url    = config.url || '';
    const isRefreshEndpoint = url.includes('/auth/session/refresh')
                           || url.includes('/auth/session/login');

    // v3.21-1769 · ACCOUNT SUSPENDED — the backend returns HTTP 403 with
    // detail `{"code":"ACCOUNT_SUSPENDED", "message":...}` when a
    // previously issued token resolves to a now-suspended user (see
    // `core.dependencies.get_current_user_from_token`).  Route the tab
    // to the dedicated `/suspended` screen and clear the local token so
    // the user cannot re-enter the workspace.
    try {
      const detail = error.response?.data?.detail;
      const suspendedCode =
        (detail && typeof detail === 'object' && detail.code === 'ACCOUNT_SUSPENDED') ||
        (typeof detail === 'string' && detail.includes('ACCOUNT_SUSPENDED'));
      if (status === 403 && suspendedCode) {
        try {
          sessionStorage.removeItem('access_token');
          localStorage.removeItem('access_token');
        } catch (_e) { /* noop */ }
        if (typeof window !== 'undefined'
            && !window.location.pathname.startsWith('/suspended')) {
          window.location.assign('/suspended');
        }
        return Promise.reject(error);
      }
    } catch (_e) { /* noop */ }

    // v3.21-1729 · Trigger the refresh path whenever we see a 401 on
    // a non-refresh call AND we have ANY local access token (memory OR
    // sessionStorage OR legacy localStorage).  This fixes the previous
    // regression where a page reload left memoryAccessToken empty and
    // the refresh path silently skipped.
    const anyLocalToken = memoryAccessToken
      || sessionStorage.getItem('access_token')
      || localStorage.getItem('access_token');
    const shouldTryRefresh = status === 401
                          && !config.__sessionRetried
                          && !isRefreshEndpoint
                          && !!anyLocalToken;

    if (shouldTryRefresh) {
      try {
        const newTok = refreshInFlight
          ? await new Promise((res) => refreshWaiters.push(res))
          : await performRefresh();
        if (!newTok) {
          fireSessionExpired();
          return Promise.reject(error);
        }
        config.__sessionRetried = true;
        // Overwrite the Authorization header with the FRESH token so the
        // retried request never carries the stale one.
        config.headers = { ...(config.headers || {}), Authorization: `Bearer ${newTok}` };
        return api(config);
      } catch (_) {
        fireSessionExpired();
        return Promise.reject(error);
      }
    }

    // Any 401 that reaches here is a true session-expired condition
    // (refresh failed, or endpoint is the refresh route itself).  Do
    // NOT auto-clear or auto-redirect — let AuthContext handle it via
    // the registered session-expired handler.
    if (status === 401 && !isRefreshEndpoint) {
      fireSessionExpired();
      return Promise.reject(error);
    }

    // Network-error retry
    const isNetworkError = !error.response && error.message?.includes('Network Error');
    const isTimeout      = error.code === 'ECONNABORTED';
    const shouldRetry    = (isNetworkError || isTimeout) && config.__retryCount < MAX_RETRIES;
    if (shouldRetry) {
      config.__retryCount += 1;
      await new Promise(r => setTimeout(r, RETRY_DELAY));
      return api(config);
    }

    failedRequests.push({
      requestId: config.__requestId, url, method: config.method,
      responseStatus: status, retries: config.__retryCount || 0,
      timestamp: new Date().toISOString(),
    });
    if (failedRequests.length > 10) failedRequests.shift();
    return Promise.reject(error);
  }
);

export const getRecentFailures = () => [...failedRequests];
export default api;
